import React, { useState, useMemo } from "react";

function formatDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function StatusBadge({ daysRemaining, noExpiry }) {
  if (noExpiry) return <span style={{ fontSize: "11px", color: "#79749c" }}>No expiry</span>;
  if (daysRemaining < 0) return <span style={{ padding: "3px 8px", borderRadius: "4px", fontSize: "11px", fontWeight: 600, background: "#f5e8ea", color: "#82162d" }}>Expired</span>;
  if (daysRemaining <= 7) return <span style={{ padding: "3px 8px", borderRadius: "4px", fontSize: "11px", fontWeight: 600, background: "#f5e8ea", color: "#82162d" }}>{daysRemaining}d</span>;
  if (daysRemaining <= 30) return <span style={{ padding: "3px 8px", borderRadius: "4px", fontSize: "11px", fontWeight: 600, background: "#faeeda", color: "#854F0B" }}>{daysRemaining}d</span>;
  return <span style={{ padding: "3px 8px", borderRadius: "4px", fontSize: "11px", fontWeight: 600, background: "#e8f5e9", color: "#2e7d32" }}>{daysRemaining}d</span>;
}

function exportToCsv(contractors) {
  const headers = ["Name", "Email", "Manager", "Department", "Job Title", "Contracting Organisation", "Created", "End Date", "Days Remaining"];
  const rows = contractors.map(c => [
    c.name, c.email, c.manager, c.department, c.jobTitle, c.company,
    c.created,
    c.endDate ? formatDate(c.endDate) : "",
    c.noExpiry ? "No expiry" : c.daysRemaining < 0 ? "Expired" : c.daysRemaining
  ]);
  const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `all-contractors-${new Date().toISOString().split("T")[0]}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function AllContractorsTable({ contractors }) {
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    if (!search.trim()) return contractors;
    const q = search.toLowerCase();
    return contractors.filter(c =>
      [c.name, c.email, c.manager, c.department, c.jobTitle, c.company].some(v =>
        (v || "").toLowerCase().includes(q)
      )
    );
  }, [contractors, search]);

  const thStyle = {
    textAlign: "left", padding: "10px 16px", fontSize: "9px", fontWeight: 700,
    color: "#b0adc8", textTransform: "uppercase", letterSpacing: "0.08em",
    borderBottom: "0.5px solid #f0f0ef", background: "#fafaf8", whiteSpace: "nowrap"
  };
  const tdStyle = { padding: "10px 16px", fontSize: "12px", color: "#18073a", borderBottom: "0.5px solid #f0f0ef", whiteSpace: "nowrap" };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px", gap: "12px" }}>
        <input
          type="text"
          placeholder="Search by name, email, manager, department, title or contracting Organisation…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ flex: 1, padding: "8px 14px", border: "0.5px solid #e0e0df", borderRadius: "6px", fontSize: "12px", fontFamily: "Montserrat, sans-serif", color: "#18073a", outline: "none" }}
        />
        <button
          onClick={() => exportToCsv(filtered)}
          style={{ padding: "8px 16px", fontSize: "12px", fontWeight: 600, background: "#f0f0ef", color: "#18073a", border: "none", borderRadius: "6px", cursor: "pointer", fontFamily: "Montserrat, sans-serif", whiteSpace: "nowrap" }}
        >
          Export CSV
        </button>
      </div>

      <div style={{ background: "#fff", borderRadius: "8px", overflow: "hidden", border: "0.5px solid #e0e0df" }}>
        {filtered.length === 0 ? (
          <div style={{ padding: "40px", textAlign: "center", color: "#79749c", fontSize: "13px" }}>No contractors match your search.</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={thStyle}>Name</th>
                  <th style={thStyle}>Email</th>
                  <th style={thStyle}>Manager</th>
                  <th style={thStyle}>Department</th>
                  <th style={thStyle}>Job Title</th>
                  <th style={thStyle}>Contracting Organisation</th>
                  <th style={thStyle}>Created</th>
                  <th style={thStyle}>End Date</th>
                  <th style={thStyle}>Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(c => (
                  <tr key={c.id} style={{ background: c.isLeaver ? "#fdf8f8" : "transparent" }}>
                    <td style={tdStyle}>{c.name}</td>
                    <td style={{ ...tdStyle, color: "#79749c" }}>{c.email}</td>
                    <td style={tdStyle}>{c.manager || "—"}</td>
                    <td style={{ ...tdStyle, color: "#79749c" }}>{c.department || "—"}</td>
                    <td style={{ ...tdStyle, color: "#79749c" }}>{c.jobTitle || "—"}</td>
                    <td style={{ ...tdStyle, color: "#79749c" }}>{c.company || "—"}</td>
                    <td style={{ ...tdStyle, color: "#79749c" }}>{c.created}</td>
                    <td style={tdStyle}>{formatDate(c.endDate)}</td>
                    <td style={tdStyle}><StatusBadge daysRemaining={c.daysRemaining} noExpiry={c.noExpiry} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <div style={{ marginTop: "10px", fontSize: "11px", color: "#b0adc8" }}>
        {filtered.length} contractor{filtered.length !== 1 ? "s" : ""}{search ? ` matching "${search}"` : " total"}
      </div>
    </div>
  );
}

export default AllContractorsTable;